import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import appConfig from '../config/appConfig';
import { isAuthenticated, getActiveRole } from '../utils/auth';
import { useBranding } from '../context/BrandingContext';
import { useRegionalSettings } from '../context/RegionalSettingsContext';
import { getAcademyTimezoneLabel, academyYear } from '../utils/regionalFormat';

const DEFAULT_TAGLINE = 'Manage. Monitor. Grow.';

function MailIcon() {
    return (
        <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
    );
}

function PhoneIcon() {
    return (
        <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
    );
}

function AcademyContact({ supportEmail, supportPhone, compact = false }) {
    const { t } = useTranslation();
    if (!supportEmail && !supportPhone) {
        return compact ? null : (
            <p className="text-sm text-gray-500">{t('footer.contactDetailsHint')}</p>
        );
    }

    return (
        <div className={`space-y-2 text-sm text-gray-400 ${compact ? '' : 'mt-4'}`}>
            {supportEmail && (
                <a href={`mailto:${supportEmail}`} className="flex items-center gap-2 hover:text-primary-400 transition-colors">
                    <MailIcon />
                    <span>{supportEmail}</span>
                </a>
            )}
            {supportPhone && (
                <a href={`tel:${supportPhone.replace(/\s+/g, '')}`} className="flex items-center gap-2 hover:text-primary-400 transition-colors">
                    <PhoneIcon />
                    <span>{supportPhone}</span>
                </a>
            )}
        </div>
    );
}

function PublicQuickLinks() {
    const { t } = useTranslation();
    return (
        <ul className="space-y-2 text-sm text-gray-400">
            <li>
                <Link to="/privacy-policy" className="hover:text-primary-400 transition-colors">
                    {t('footer.privacyPolicy')}
                </Link>
            </li>
            <li>
                <Link to="/terms" className="hover:text-primary-400 transition-colors">
                    {t('footer.termsOfService')}
                </Link>
            </li>
            <li>
                <Link to="/forgot-password" className="hover:text-primary-400 transition-colors">
                    {t('footer.forgotPassword')}
                </Link>
            </li>
            <li>
                <Link to="/help" className="hover:text-primary-400 transition-colors">
                    {t('footer.helpGettingStarted')}
                </Link>
            </li>
        </ul>
    );
}

function AuthenticatedQuickLinks() {
    const { t } = useTranslation();
    const role = getActiveRole();
    const faqPath = useMemo(() => {
        switch (role) {
            case 'MANAGER': return '/manager/faq';
            case 'COACH': return '/coach/faq';
            case 'STUDENT': return '/student/faq';
            default: return '/admin/faq';
        }
    }, [role]);

    return (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-400">
            <li>
                <Link to={faqPath} className="hover:text-primary-400 transition-colors">
                    {t('footer.helpFaq')}
                </Link>
            </li>
            <li>
                <Link to="/privacy-policy" className="hover:text-primary-400 transition-colors">
                    {t('footer.privacyPolicy')}
                </Link>
                <span className="mx-2 text-gray-600">·</span>
                <Link to="/terms" className="hover:text-primary-400 transition-colors">
                    {t('footer.terms')}
                </Link>
            </li>
        </ul>
    );
}

function VendorCredit({ className = '' }) {
    const { t } = useTranslation();
    const { vendorName, vendorEmail, vendorUrl, vendorPhone } = appConfig;
    if (!vendorName && !vendorEmail && !vendorPhone) {
        return null;
    }

    const vendorNameEl = vendorName && (
        vendorUrl ? (
            <a
                href={vendorUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-400 transition-colors"
            >
                {vendorName}
            </a>
        ) : (
            <span className="text-gray-400">{vendorName}</span>
        )
    );

    return (
        <p className={`text-xs text-gray-500 ${className}`}>
            {t('footer.developedBy')}{' '}
            {vendorNameEl}
            {vendorEmail && (
                <>
                    {vendorName ? ' · ' : ''}
                    <a href={`mailto:${vendorEmail}`} className="hover:text-primary-400 transition-colors">
                        {vendorEmail}
                    </a>
                </>
            )}
            {vendorPhone && !vendorEmail && (
                <>
                    {vendorName ? ' · ' : ''}
                    <a href={`tel:${vendorPhone.replace(/\s+/g, '')}`} className="hover:text-primary-400 transition-colors">
                        {vendorPhone}
                    </a>
                </>
            )}
        </p>
    );
}

function SectionLabel({ children }) {
    return (
        <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
            {children}
        </h4>
    );
}

function LicenseInfoBlock({ licenseInfo, tzLabel }) {
    const { t } = useTranslation();
    if (!licenseInfo) {
        return <p className="text-sm text-gray-500">{t('footer.licensePending')}</p>;
    }

    return (
        <div className="space-y-1.5 text-sm text-gray-400">
            <div className="flex items-center gap-2">
                <svg className="h-4 w-4 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                <span className="truncate font-mono text-xs tracking-wide">{licenseInfo.maskedKey}</span>
            </div>
            <div className="flex items-center gap-2">
                <svg className="h-4 w-4 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {licenseInfo.daysRemaining >= 0 ? (
                    <span className={licenseInfo.daysRemaining <= 30 ? 'text-yellow-400' : 'text-gray-400'}>
                        {t('footer.daysRemaining', { count: licenseInfo.daysRemaining })}
                    </span>
                ) : (
                    <span>{t('footer.licenseActive')}</span>
                )}
            </div>
            <p className="text-xs text-gray-500 font-mono">
                {t('footer.built', {
                    version: licenseInfo.versionLabel || licenseInfo.version || t('footer.unknownVersion'),
                    tz: tzLabel || getAcademyTimezoneLabel(),
                })}
                {licenseInfo.channel && (
                    <span className={`ml-1 font-sans ${licenseInfo.channel === 'release' ? 'text-green-400/90' : 'text-amber-400/90'}`}>
                        · {licenseInfo.channel}
                    </span>
                )}
            </p>
        </div>
    );
}

const Footer = () => {
    const { t } = useTranslation();
    const authed = isAuthenticated();
    const { settings } = useRegionalSettings();
    const { appName, tagline, supportEmail, supportPhone } = useBranding();
    const [licenseInfo, setLicenseInfo] = useState(null);
    const rawTagline = tagline || DEFAULT_TAGLINE;
    const displayTagline = (rawTagline === DEFAULT_TAGLINE || rawTagline === appConfig.tagline)
        ? t('brand.tagline')
        : rawTagline;
    const hasAcademyContact = Boolean(supportEmail || supportPhone);

    useEffect(() => {
        if (!authed) {
            setLicenseInfo(null);
            return;
        }

        axios.get('/api/public/license/info')
            .then((res) => setLicenseInfo(res.data))
            .catch(() => setLicenseInfo(null));
    }, [authed]);

    return (
        <footer className="mt-auto border-t border-white/10 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-gray-300">
            <div className={`${authed ? 'mx-auto max-w-7xl px-4 pb-2 sm:px-6' : 'max-w-7xl mx-auto px-6'} py-0`}>
                <div className="py-3">
                    {authed ? (
                        <div className="grid gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:items-start">
                            <div>
                                <h3 className="text-xl font-bold tracking-tight text-white">{appName}</h3>
                                <p className="mt-1 max-w-sm text-sm text-gray-400">{displayTagline}</p>
                                <div className="mt-3">
                                    <SectionLabel>{t('footer.quickLinks')}</SectionLabel>
                                    <AuthenticatedQuickLinks />
                                </div>
                            </div>

                            <div className={`grid gap-5 ${hasAcademyContact ? 'sm:grid-cols-2' : 'sm:grid-cols-1'}`}>
                                {hasAcademyContact && (
                                    <div>
                                        <SectionLabel>{t('footer.academyContact')}</SectionLabel>
                                        <AcademyContact supportEmail={supportEmail} supportPhone={supportPhone} compact />
                                    </div>
                                )}
                                <div className={hasAcademyContact ? '' : 'sm:col-span-1 sm:justify-self-end sm:max-w-xs'}>
                                    <SectionLabel>{t('footer.licenseInfo')}</SectionLabel>
                                    <LicenseInfoBlock licenseInfo={licenseInfo} tzLabel={settings?.timezone ? getAcademyTimezoneLabel() : ''} />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className={`grid gap-8 ${hasAcademyContact ? 'md:grid-cols-2' : 'md:grid-cols-[minmax(0,1fr)_auto]'}`}>
                            <div>
                                <h3 className="text-base font-bold text-white">{appName}</h3>
                                <p className="mt-1.5 max-w-md text-sm leading-relaxed text-gray-400">
                                    {displayTagline}
                                </p>
                                {hasAcademyContact && (
                                    <div className="mt-4">
                                        <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">{t('footer.academyContact')}</h4>
                                        <AcademyContact supportEmail={supportEmail} supportPhone={supportPhone} compact />
                                    </div>
                                )}
                            </div>

                            <div className="md:justify-self-end md:min-w-[220px]">
                                <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">{t('footer.quickLinks')}</h4>
                                <PublicQuickLinks />
                            </div>
                        </div>
                    )}

                    <div className="mt-3 border-t border-gray-700 pt-2.5 text-center space-y-1">
                        <p className="text-sm text-gray-400">
                            &copy; {appConfig.copyrightYear || academyYear()} {appName}. {t('footer.allRightsReserved')}
                        </p>
                        <VendorCredit />
                        <p className="text-xs text-gray-500">{t('footer.chessManagementSystem')}</p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
